import { useEffect, useState, useMemo } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Eye, EyeOff, Copy, Trash2, KeyRound, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", category: "HMRC", username: "", url: "", notes: "", password: "" };

export default function Vault() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // entry being created/edited
  const [revealed, setRevealed] = useState({});   // { entry_id: plainPassword }
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    const r = await api.get("/vault");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.title, r.category, r.username, r.url, r.notes].filter(Boolean).some((v) => v.toLowerCase().includes(s))
    );
  }, [rows, q]);

  const save = async () => {
    try {
      if (editing.entry_id) {
        await api.patch(`/vault/${editing.entry_id}`, editing);
        toast.success("Entry updated");
      } else {
        await api.post("/vault", editing);
        toast.success("Entry added");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reveal = async (id) => {
    if (revealed[id]) {
      setRevealed((r) => { const n = { ...r }; delete n[id]; return n; });
      return;
    }
    try {
      const r = await api.get(`/vault/${id}/reveal`);
      setRevealed((old) => ({ ...old, [id]: r.data.password || "" }));
    } catch { toast.error("Reveal failed"); }
  };

  const copyPw = async (id) => {
    try {
      let pw = revealed[id];
      if (!pw) {
        const r = await api.get(`/vault/${id}/reveal`);
        pw = r.data.password || "";
      }
      if (!pw) { toast.error("Nothing to copy"); return; }
      await navigator.clipboard.writeText(pw);
      toast.success("Password copied");
    } catch { toast.error("Copy failed"); }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/vault/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => {
    const ids = filtered.map((r) => r.entry_id);
    const all = ids.every((i) => s.has(i));
    if (all) { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; }
    const n = new Set(s); ids.forEach((i) => n.add(i)); return n;
  });
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/vault/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} entr${r.data.deleted === 1 ? "y" : "ies"}`);
      clearSel();
      setBulkDeleteOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBulkDeleting(false); }
  };

  return (
    <div>
      <PageHeader
        testid="page-vault"
        title="Personal vault"
        subtitle="Encrypted at rest · Admin-only · Passwords never appear in logs"
        actions={
          <Button data-testid="vault-add-btn" onClick={() => setEditing({ ...EMPTY })} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-1" /> Add entry
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="vault-search" className="pl-9 w-80 bg-white" placeholder="Search title, category, notes…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="text-sm text-slate-500">{filtered.length} entr{filtered.length === 1 ? "y" : "ies"}</div>
        </div>

        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div data-testid="vault-bulk-bar" className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm">
              <span data-testid="vault-selected-count"><span className="font-semibold">{selected.size}</span> selected</span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearSel} className="text-slate-300 hover:text-white text-xs">Clear</button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-8" data-testid="vault-bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2">
                  <Checkbox
                    data-testid="vault-select-all"
                    checked={filtered.length > 0 && filtered.every((r) => selected.has(r.entry_id))}
                    onCheckedChange={toggleAll}
                    aria-label="Select all vault entries"
                  />
                </th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Username / ID</th>
                <th className="text-left px-4 py-2">Password</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500">
                  <KeyRound className="h-6 w-6 mx-auto text-slate-300 mb-2" />
                  Nothing here yet. Click <span className="font-medium">Add entry</span> to store your first credential.
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.entry_id} data-testid="vault-row" className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(r.entry_id) ? "bg-slate-50" : ""}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      data-testid={`vault-row-select-${r.entry_id}`}
                      checked={selected.has(r.entry_id)}
                      onCheckedChange={() => toggleSel(r.entry_id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setEditing({ ...r, password: "" })} className="text-slate-900 font-medium hover:text-blue-600 transition-colors text-left">
                      {r.title}
                    </button>
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center text-slate-400 hover:text-blue-600">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {r.notes && <div className="text-xs text-slate-500 truncate max-w-md">{r.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.category || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.username || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {r.password_present ? (
                      revealed[r.entry_id] ? <span className="text-slate-900">{revealed[r.entry_id]}</span> : <span className="text-slate-400">••••••••••••</span>
                    ) : <span className="text-slate-300">no password</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {r.password_present && (
                        <>
                          <button onClick={() => reveal(r.entry_id)} title={revealed[r.entry_id] ? "Hide" : "Reveal"} className="p-1.5 text-slate-500 hover:text-slate-900 rounded transition-colors" data-testid={`vault-reveal-${r.entry_id}`}>
                            {revealed[r.entry_id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          <button onClick={() => copyPw(r.entry_id)} title="Copy password" className="p-1.5 text-slate-500 hover:text-slate-900 rounded transition-colors" data-testid={`vault-copy-${r.entry_id}`}>
                            <Copy className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button onClick={() => removeOne(r.entry_id)} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-600 rounded transition-colors" data-testid={`vault-delete-${r.entry_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="vault-editor" className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.entry_id ? "Edit entry" : "Add vault entry"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Title *</Label>
                  <Input data-testid="vault-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input data-testid="vault-category-input" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="HMRC · Gateway · Software · Other…" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Username / User ID</Label>
                  <Input data-testid="vault-username-input" value={editing.username || ""} onChange={(e) => setEditing({ ...editing, username: e.target.value })} />
                </div>
                <div>
                  <Label>{editing.entry_id ? "New password (leave blank to keep)" : "Password"}</Label>
                  <Input data-testid="vault-password-input" type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>URL</Label>
                <Input data-testid="vault-url-input" value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://…" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea data-testid="vault-notes-input" rows={4} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <div className="text-[11px] text-slate-500">Password is encrypted at rest using symmetric encryption. Only admins can view or edit vault entries.</div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="vault-save-btn" onClick={save} disabled={!editing?.title} className="bg-slate-900 hover:bg-slate-800">{editing?.entry_id ? "Save changes" : "Add entry"}</Button>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="vault-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} vault entr{selected.size === 1 ? "y" : "ies"}?</AlertDialogTitle>
            <AlertDialogDescription>
              These credentials will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} disabled={bulkDeleting} data-testid="vault-bulk-delete-confirm" className="bg-rose-600 hover:bg-rose-700">
              {bulkDeleting ? "Deleting…" : `Yes, delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
